import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

type WorkAtRiskScope = {
  hasWorkAtRisk: boolean;
  getHasWorkAtRisk: () => boolean;
  register: (id: string) => () => void;
};
const WorkAtRiskContext = createContext<WorkAtRiskScope | null>(null);

/** Unsent work outlives a page's save counters but stays scoped to its owning frame. */
export function PageWorkAtRiskProvider({ children, enabled = true }: PropsWithChildren<{ enabled?: boolean }>) {
  const [owners, setOwners] = useState<ReadonlySet<string>>(() => new Set());
  const ownersRef = useRef(owners);
  const getHasWorkAtRisk = useCallback(() => ownersRef.current.size > 0, []);
  const register = useCallback((id: string) => {
    const update = (active: boolean) => {
      const next = new Set(ownersRef.current);
      if (active) next.add(id);
      else next.delete(id);
      ownersRef.current = next;
      setOwners(next);
    };
    update(true);
    return () => update(false);
  }, []);
  const value = useMemo(
    () => ({ hasWorkAtRisk: owners.size > 0, getHasWorkAtRisk, register }),
    [owners, getHasWorkAtRisk, register]
  );
  return <WorkAtRiskContext.Provider value={enabled ? value : null}>{children}</WorkAtRiskContext.Provider>;
}

export function usePageWorkAtRisk(active: boolean): void {
  const register = useContext(WorkAtRiskContext)?.register;
  const id = useId();
  useLayoutEffect(() => (active ? register?.(id) : undefined), [active, id, register]);
}

export function useHasPageWorkAtRisk(): boolean {
  return useContext(WorkAtRiskContext)?.hasWorkAtRisk ?? false;
}

/** Read at the reload decision too: layout registrations can precede a context rerender. */
export function usePageWorkAtRiskCheck(): (() => boolean) | undefined {
  return useContext(WorkAtRiskContext)?.getHasWorkAtRisk;
}
